import React, { useState, useEffect, useRef } from 'react'
import { FiMap } from 'react-icons/fi'
import { useTranslation } from '../i18n/LanguageContext'
import CarCard from './CarCard'
import DeferredMap from './DeferredMap'
import { savedListingKey } from '../lib/savedListings.js'
import './ResultsList.css'

// SIMPLE-1: what each list is, in the buyer's words.
const SECTION = {
  koopkans: ['Buy opportunities', 'Every check passed and the dealer page confirmed the car in the last 24 hours. Still inspect it before you pay.'],
  controleer: ['Check it yourself', 'Our model sees a profit, but the dealer page could not be read automatically. Open the advert and check price, condition and location yourself.'],
  all: ['All priced cars', 'Every car we could value, including the ones that are not worth importing. Each card says why.'],
  saved: ['Saved Listings', 'Snapshots you saved on this device. Open the advert to see the current state.'],
}

function ResultsList({ lists, onLoadMore, sortBy, onSortChange, mode = 'picks', onModeChange,
  savedListingIds, onToggleSave, isSavedView = false, onLoadMapData }) {
  const { t } = useTranslation()
  const [showMap, setShowMap] = useState(false)
  const [mapData, setMapData] = useState(null)
  const [isLoadingMap, setIsLoadingMap] = useState(false)
  const [mapError, setMapError] = useState('')
  const [loadingMore, setLoadingMore] = useState(null)
  const mapRequest = useRef(0)
  const mapButton = useRef(null)
  const total = lists.reduce((sum, list) => sum + list.total, 0)
  const loaded = lists.flatMap(list => list.rows)

  // A new search invalidates the map that belonged to the old one.
  useEffect(() => {
    setMapData(null); setMapError(''); setShowMap(false); setIsLoadingMap(false)
    mapRequest.current++
    return () => { mapRequest.current++ }
  }, [lists.map(list => `${list.key}:${list.total}`).join('|'), sortBy])  // eslint-disable-line react-hooks/exhaustive-deps

  const openMap = async () => {
    const request = ++mapRequest.current
    setMapError('')
    if (onLoadMapData && !mapData) {
      setIsLoadingMap(true)
      try {
        const data = await onLoadMapData()
        if (request !== mapRequest.current) return
        if (!data.length) { setMapError('No map data is available for this selection. Your list is unchanged.'); return }
        setMapData(data)
      } catch {
        if (request === mapRequest.current) setMapError('Map unavailable. Your list is unchanged. Try View Map again.')
        return
      } finally {
        if (request === mapRequest.current) setIsLoadingMap(false)
      }
    }
    if (request === mapRequest.current) setShowMap(true)
  }
  const more = async key => {
    setLoadingMore(key)
    try { await onLoadMore(key) } finally { setLoadingMore(null) }
  }

  return (
    <div className="results-list">
      {!isSavedView && (
        <div className="results-header">
          <div className="mode-toggle" role="group" aria-label={t('What to show')}>
            <button aria-pressed={mode !== 'all'} className={mode !== 'all' ? 'on' : ''} onClick={() => onModeChange('picks')}>{t('Opportunities')}</button>
            <button aria-pressed={mode === 'all'} className={mode === 'all' ? 'on' : ''} onClick={() => onModeChange('all')}>{t('All priced cars')}</button>
          </div>
          <div className="results-controls">
            <label className="sort-controls">{t('Sort by:')}
              <select value={sortBy} onChange={e => onSortChange(e.target.value)} className="sort-select">
                <option value="safe">{t('Highest profit if the sale disappoints')}</option>
                <option value="expected">{t('Highest expected profit')}</option>
                <option value="price_low">{t('Lowest price')}</option>
                <option value="newest">{t('Newest advert')}</option>
              </select>
            </label>
            {total > 0 && (
              <button className="view-map-btn" ref={mapButton} onClick={openMap} disabled={isLoadingMap}>
                <FiMap /> {isLoadingMap ? t('Loading...') : t('View Map')}
              </button>
            )}
          </div>
        </div>
      )}

      {showMap && (mapData || loaded).length > 0 && (
        <DeferredMap cars={mapData || loaded} isSavedSnapshot={isSavedView}
          onClose={() => { setShowMap(false); mapButton.current?.focus() }}
          savedListingIds={savedListingIds} onToggleSave={onToggleSave} />
      )}
      {mapError && <p className="map-error" role="status">{t(mapError)}</p>}

      {total === 0 && (
        <div className="no-results-message" role="status">
          <h3>{t('No cars found')}</h3>
          <p>{mode !== 'all' && !isSavedView
            ? t('No opportunity matches these filters today. Widen the filters, or look at all priced cars.')
            : t('Try adjusting your filters to see more cars.')}</p>
        </div>
      )}

      {lists.filter(list => list.total > 0).map(list => (
        <section key={list.key} className="pick-section" aria-label={t(SECTION[list.key][0])}>
          <h2>{t(SECTION[list.key][0])} <span className="results-count">{list.total.toLocaleString('nl-NL')}</span></h2>
          <p>{t(SECTION[list.key][1])}</p>
          <div className="cars-grid">
            {list.rows.map(car => (
              <CarCard key={car.id} car={car} isSavedSnapshot={isSavedView}
                isSaved={savedListingIds ? savedListingIds.has(savedListingKey(car)) : false}
                onToggleSave={onToggleSave} />
            ))}
          </div>
          {onLoadMore && list.rows.length < list.total && (
            <button className="load-more" disabled={loadingMore === list.key} onClick={() => more(list.key)}>
              {loadingMore === list.key ? t('Loading...')
                : t('Show more ({shown} of {total})', { shown: list.rows.length, total: list.total.toLocaleString('nl-NL') })}
            </button>
          )}
        </section>
      ))}
    </div>
  )
}

export default ResultsList
